import React from "react";
import { DraftPreviewProps } from "./types";
import { DraftActionButtons } from "./DraftActionButtons";
import { DraftStatusBadge } from "./DraftStatusBadge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  X,
  Eye,
  User,
  Calendar,
  GitCommit,
  FileText,
  Info,
  Clock,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";

const getUserInitials = (name: string): string => {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

export const DraftPreview: React.FC<DraftPreviewProps> = ({
  draft,
  onClose,
  onEdit,
  onPublish,
  onDelete,
}) => {
  const userInitials = getUserInitials(draft.createdBy);
  const timeAgo = formatDistanceToNow(draft.updatedAt, { addSuffix: true });
  const formattedDate = format(draft.createdAt, "PPpp");

  const changeCount = draft.changes?.length || 0;

  // Group changes by type for summary
  const changeSummary =
    draft.changes?.reduce(
      (acc, change) => {
        acc[change.type] = (acc[change.type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    ) || {};

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Eye className="w-5 h-5" />
              <DialogTitle className="text-xl font-bold">
                {draft.name}
              </DialogTitle>
              <DraftStatusBadge status={draft.status} />
            </div>

            <DialogClose asChild>
              <Button variant="outline" size="sm">
                <X className="w-4 h-4" />
              </Button>
            </DialogClose>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 px-6">
          <div className="space-y-6 pb-6">
            {/* Draft Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-base">
                  <Info className="w-4 h-4" />
                  <span>Draft Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Description */}
                {draft.description && (
                  <div>
                    <h4 className="font-medium text-sm mb-2">Description</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {draft.description}
                    </p>
                  </div>
                )}

                {/* Metadata */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Author */}
                  <div className="flex items-center space-x-3">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="text-sm">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm">{draft.createdBy}</p>
                      <p className="text-xs text-muted-foreground">Author</p>
                    </div>
                  </div>

                  {/* Created Date */}
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                      <Calendar className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {formatDistanceToNow(draft.createdAt, {
                          addSuffix: true,
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formattedDate}
                      </p>
                    </div>
                  </div>

                  {/* Last Updated */}
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                      <Clock className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{timeAgo}</p>
                      <p className="text-xs text-muted-foreground">
                        Last updated
                      </p>
                    </div>
                  </div>

                  {/* Base Version */}
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        v{draft.baseVersion}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Base Version
                      </p>
                    </div>
                  </div>
                </div>

                {/* Branch Info */}
                {draft.branchId && (
                  <div>
                    <h4 className="font-medium text-sm mb-2">Branch</h4>
                    <Badge variant="outline" className="text-xs">
                      {draft.branchId}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Changes Summary */}
            {changeCount > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 text-base">
                    <GitCommit className="w-4 h-4" />
                    <span>Changes ({changeCount})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    {Object.entries(changeSummary).map(([type, count]) => (
                      <div
                        key={type}
                        className="text-center p-3 bg-muted/50 rounded-lg"
                      >
                        <div className="text-lg font-bold">{count}</div>
                        <div className="text-sm text-muted-foreground capitalize">
                          {type}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Recent Changes */}
                  <div>
                    <h4 className="font-medium text-sm mb-3">Recent Changes</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {draft.changes?.slice(0, 10).map((change, index) => (
                        <div
                          key={change.id || index}
                          className="flex items-center space-x-3 p-2 rounded-md bg-muted/30"
                        >
                          <div
                            className={`w-2 h-2 rounded-full ${
                              change.type === "create"
                                ? "bg-green-500"
                                : change.type === "update"
                                  ? "bg-blue-500"
                                  : change.type === "delete"
                                    ? "bg-red-500"
                                    : "bg-gray-500"
                            }`}
                          />
                          <div className="flex-1">
                            <div className="text-sm">
                              <span className="font-medium capitalize">
                                {change.type}
                              </span>
                              <span className="ml-1">{change.target}</span>
                              {change.description && (
                                <span className="text-muted-foreground ml-1">
                                  - {change.description}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {format(change.timestamp, "MMM d, yyyy HH:mm")}
                            </div>
                          </div>
                        </div>
                      ))}
                      {draft.changes && draft.changes.length > 10 && (
                        <div className="text-center text-sm text-muted-foreground py-2">
                          +{draft.changes.length - 10} more changes
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* No Changes */}
            {changeCount === 0 && (
              <Card>
                <CardContent className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <GitCommit className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No changes recorded in this draft yet
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>

        {/* Actions Footer */}
        <div className="p-6 pt-0 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <FileText className="w-4 h-4" />
              <span>Draft preview - {draft.name}</span>
            </div>

            <DraftActionButtons
              draft={draft}
              showEdit={!!onEdit}
              showPublish={!!onPublish}
              showDelete={!!onDelete}
              showReview={false}
              showPreview={false}
              onEdit={onEdit}
              onPublish={onPublish}
              onDelete={onDelete}
              size="sm"
              variant="inline"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DraftPreview;
